/**
 * useCmsLectures — the published lecture list, fresher than the bundle.
 *
 * WHY THIS EXISTS
 * ---------------
 * `cmsLectures` is a build-time snapshot written by `scripts/cms-sync.ts` and
 * compiled into the bundle. Until `npm run build` runs again it cannot see a
 * lecture published after the last deploy — the 2026-10-16 "published a lecture
 * in the CMS, it shows on the dev box at :3006 but not on prod" incident. The
 * operator has no signal that anything is missing; the archive just quietly
 * omits the newest entries.
 *
 * The runtime API (`/api/cms/public/lectures`) is already the source of truth
 * for `LectureDetailPage`, so it costs one request to make the archive and the
 * homepage agree with the detail page.
 *
 * WHY KEEP THE SNAPSHOT AT ALL
 * ----------------------------
 * Two reasons, both load-bearing:
 *
 *   1. Prerendered HTML. `scripts/prerender.tsx` renders every route at build
 *      time from `cmsLectures`. This hook's `useState` seed IS that snapshot,
 *      so the first paint (and anything a non-JS crawler sees) is complete
 *      before any request goes out.
 *   2. Graceful degradation. If the API is slow or the CMS is down, we keep
 *      the last-known-good list instead of flashing an empty page.
 *
 * This mirrors `HarbourTeaser.tsx`, which already does exactly this for
 * `cms_product_pages`.
 *
 * WHY NOT RE-SORT HERE
 * --------------------
 * The endpoint already returns `ORDER BY event_date DESC, id DESC`
 * (server/routes/public.ts). Sorting client-side would duplicate that logic
 * and let the two drift — which is exactly the bug `MagazinePage` had to work
 * around by re-sorting for itself. Consumers that need a different order (a
 * per-audience slice, say) can sort the returned array; nothing here promises
 * an order the server didn't already apply.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsLectures, type CmsLectureIndexEntry } from '@/content/cms/lectures.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'

interface LecturesResp {
  lectures?: CmsLectureIndexEntry[]
}

export function useCmsLectures(): CmsLectureIndexEntry[] {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  // Seeded from the build-time snapshot — this is the value that gets
  // prerendered into the HTML, and the value we keep if the fetch fails.
  const [items, setItems] = useState<CmsLectureIndexEntry[]>(cmsLectures)

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/lectures?locale=${encodeURIComponent(locale)}${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<LecturesResp>) : null))
      .then((j) => {
        if (cancelled) return
        // An empty array means the CMS has no published lectures, which is a
        // legitimate operator state (mid-migration, everything unpublished).
        // It is NOT a reason to blank a page that the bundle can still render
        // correctly, so we only take the live payload when it has content.
        if (!j?.lectures?.length) return
        setItems(j.lectures)
      })
      .catch(() => {
        // Network error, abort, or non-JSON response. The snapshot stays.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale])

  return items
}